import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Space, message } from 'antd';
import { LockOutlined, CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { authApi } from '@/services/api';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (values: { token?: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      const resetToken = token || values.token;
      if (!resetToken) {
        message.error('缺少重置令牌');
        return;
      }

      await authApi.resetPassword(resetToken, values.newPassword);
      message.success('密码重置成功！');
      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '密码重置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="login-gradient" />
        </div>

        <div className="login-content">
          <Card className="login-card" style={{ maxWidth: '500px', textAlign: 'center' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
              <Title level={3}>密码重置成功！</Title>
              <Text type="secondary">即将跳转到登录页面...</Text>
              <Button type="primary" onClick={() => navigate('/login')} size="large">
                立即登录
              </Button>
            </Space>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-gradient" />
      </div>

      <div className="login-content">
        <Card className="login-card" style={{ maxWidth: '500px' }}>
          <div style={{ marginBottom: '24px' }}>
            <Link to="/login" style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
              <ArrowLeftOutlined style={{ marginRight: '4px' }} />
              返回登录
            </Link>
          </div>

          <div className="login-header">
            <Title level={2} className="login-title">
              重置密码
            </Title>
            <Text type="secondary" className="login-subtitle">
              请设置新密码
            </Text>
          </div>

          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
            style={{ marginTop: '32px' }}
          >
            {!token && (
              <Form.Item
                name="token"
                label="重置令牌"
                rules={[{ required: true, message: '请输入重置令牌' }]}
              >
                <Input placeholder="请输入重置令牌" />
              </Form.Item>
            )}

            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码至少6个字符' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/,
                  message: '密码必须包含大小写字母和数字',
                },
              ]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入新密码"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认密码"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请再次输入新密码"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                重置密码
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              密码要求：至少6个字符，包含大小写字母和数字
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
